import { map, Observable, switchMap, of, forkJoin } from 'rxjs';
import {
  ActivateBakery2bClientMutation,
  ActivateBakery2bClientMutationVariables,
  AddCustomerConnectedWithBakery2BClientMutation,
  AddCustomerConnectedWithBakery2BClientMutationVariables,
  DeactivateBakery2bClientMutation,
  DeactivateBakery2bClientMutationVariables,
  DeleteBakery2bClientMutation,
  DeleteBakery2bClientMutationVariables,
  DeleteCustomerConnectedWithBakery2BClientMutation,
  DeleteCustomerConnectedWithBakery2BClientMutationVariables,
  GetWa_Bakery2bSettingsQuery,
  ListBakery2bQuery,
  ListCustomerConnectedWithBakery2bClientQuery,
  ListCustomerConnectedWithBakery2bClientQueryVariables,
  ReinviteBakery2bClientMutation,
  ReinviteBakery2bClientMutationVariables,
  SaveBakery2bClientMutation,
  SaveBakery2bClientMutationVariables,
  SaveWa_Bakery2bSettingsMutation,
  SaveWa_Bakery2bSettingsMutationVariables,
  ListBakery2bStatisticQueryVariables,
  ListBakery2bStatisticQuery,
  SaveConfigDataMutation,
  SaveConfigDataMutationVariables,
  C_Save_Operation_Status,
  Wa_SaveBakery2bSettings,
  Wa_SaveConfigInput,
} from '../../../../graphql/generatedModel';
import { Pub, Service, Sub } from '../../../../shared/services/service.abstract';
import { gqlClient } from '../../../../graphql/graphqlRequest';
import {
  activateBakery2bClient,
  addCustomerConnectedWithBakery2BClient,
  deactivateBakery2bClient,
  deleteBakery2bClient,
  deleteCustomerConnectedWithBakery2BClient,
  getBakery2bSettings,
  listBakery2bClient,
  listCustomerConnectedWithBakery2bClient,
  reinviteBakery2bClient,
  saveBakery2bClient,
  saveBakery2bSettings,
  listBakery2bStatistic,
} from './gql/bakery2b.gql';
import { saveConfigData } from '../../../../shared/services/companyConfig/gql/getConfigs.gql';

type Actions =
  | 'getBakery2bSettings'
  | 'saveBakery2bSettings'
  | 'listBakery2bClient'
  | 'saveBakery2bClient'
  | 'activateBakery2bClient'
  | 'addCustomerConnectedWithBakery2BClient'
  | 'deactivateBakery2bClient'
  | 'deleteBakery2bClient'
  | 'deleteCustomerConnectedWithBakery2BClient'
  | 'listCustomerConnectedWithBakery2bClient'
  | 'reinviteBakery2bClient'
  | 'listBakery2bStatistic'
  | undefined;

class PubImpl extends Pub<Actions> {
  getBakery2bSettings() {
    this.emit('getBakery2bSettings');
  }
  saveBakery2bSettings(params: SaveBakery2bSettingsInput) {
    this.emit('saveBakery2bSettings', params);
  }
  listBakery2bClient(): void {
    this.emit('listBakery2bClient');
  }
  saveBakery2bClient(params: SaveBakery2bClientMutationVariables): void {
    this.emit('saveBakery2bClient', params);
  }
  activateBakery2bClient(params: ActivateBakery2bClientMutationVariables): void {
    this.emit('activateBakery2bClient', params);
  }
  deactivateBakery2bClient(params: DeactivateBakery2bClientMutationVariables): void {
    this.emit('deactivateBakery2bClient', params);
  }
  addCustomerConnectedWithBakery2BClient(
    params: AddCustomerConnectedWithBakery2BClientMutationVariables,
  ): void {
    this.emit('addCustomerConnectedWithBakery2BClient', params);
  }
  deleteBakery2bClient(params: DeleteBakery2bClientMutationVariables): void {
    this.emit('deleteBakery2bClient', params);
  }
  deleteCustomerConnectedWithBakery2BClient(
    params: DeleteCustomerConnectedWithBakery2BClientMutationVariables,
  ): void {
    this.emit('deleteCustomerConnectedWithBakery2BClient', params);
  }
  listCustomerConnectedWithBakery2bClient(
    params: ListCustomerConnectedWithBakery2bClientQueryVariables,
  ) {
    this.emit('listCustomerConnectedWithBakery2bClient', params);
  }
  reinviteBakery2bClient(params: ReinviteBakery2bClientMutationVariables): void {
    this.emit('reinviteBakery2bClient', params);
  }
  listBakery2bStatistic(params: ListBakery2bStatisticQueryVariables): void {
    this.emit('listBakery2bStatistic', params);
  }
}

class SubImpl extends Sub<Actions> {
  getBakery2bSettings(): Observable<GetBakery2bSettingsQueryRes> {
    return this.actionListener('getBakery2bSettings').pipe(
      switchMap(() => {
        return gqlClient(getBakery2bSettings) as Observable<GetWa_Bakery2bSettingsQuery>;
      }),
      map((data) => {
        const settings = data.wawiAssist?.getWA_Bakery2bSettings;
        const config = data.wawiAssist?.getConfigData;
        return {
          ...(settings ?? {}),
          config: config ?? {},
        } as GetBakery2bSettingsQueryRes;
      }),
    );
  }

  saveBakery2bSettings(): Observable<SaveAllBakery2bSettingsMutationRes> {
    return this.actionListener('saveBakery2bSettings').pipe(
      switchMap(({ params }) => {
        const { config, ...bakeryOnlyParams } = params;

        const bakeryParams: SaveWa_Bakery2bSettingsMutationVariables | null = hasOwnKeys(
          bakeryOnlyParams,
        )
          ? { params: bakeryOnlyParams }
          : null;

        const configParams: SaveConfigDataMutationVariables | null =
          config && hasOwnKeys(config) ? { dataToSave: config } : null;

        return forkJoin({
          saveWA_Bakery2bSettings: bakeryParams
            ? (
                gqlClient(
                  saveBakery2bSettings,
                  bakeryParams,
                ) as Observable<SaveWa_Bakery2bSettingsMutation>
              ).pipe(
                map(
                  (data) =>
                    data.wawiAssist?.saveWA_Bakery2bSettings as SaveBakery2bSettingsMutationRes,
                ),
              )
            : of(false),
          saveConfigData: configParams
            ? (gqlClient(saveConfigData, configParams) as Observable<SaveConfigDataMutation>).pipe(
                map((data) => data.wawiAssist?.saveConfigData as SaveConfigDataMutationRes),
              )
            : of({ status: C_Save_Operation_Status.SOS0_DATA_UNCHANGED }),
        });
      }),
    );
  }

  listBakery2bClient(): Observable<ListBakery2bQueryRes> {
    return this.actionListener('listBakery2bClient').pipe(
      switchMap(() => gqlClient(listBakery2bClient) as Observable<ListBakery2bQuery>),
      map((data) => {
        return data.wawiAssist?.listWA_Bakery2b as ListBakery2bQueryRes;
      }),
    );
  }
  saveBakery2bClient(): Observable<SaveBakery2bClientMutationRes> {
    return this.actionListener('saveBakery2bClient').pipe(
      switchMap(
        ({ params }) =>
          gqlClient(saveBakery2bClient, params) as Observable<SaveBakery2bClientMutation>,
      ),
      map((data) => data.wawiAssist?.saveWA_Bakery2bClient as SaveBakery2bClientMutationRes),
    );
  }
  activateBakery2bClient(): Observable<ActivateBakery2bClientMutationRes> {
    return this.actionListener('activateBakery2bClient').pipe(
      switchMap(
        ({ params }) =>
          gqlClient(activateBakery2bClient, params) as Observable<ActivateBakery2bClientMutation>,
      ),
      map(
        (data) => data.wawiAssist?.activateWA_Bakery2bClient as ActivateBakery2bClientMutationRes,
      ),
    );
  }
  deactivateBakery2bClient(): Observable<DeactivateBakery2bClientMutationRes> {
    return this.actionListener('deactivateBakery2bClient').pipe(
      switchMap(
        ({ params }) =>
          gqlClient(
            deactivateBakery2bClient,
            params,
          ) as Observable<DeactivateBakery2bClientMutation>,
      ),
      map(
        (data) =>
          data.wawiAssist?.deactivateWA_Bakery2bClient as DeactivateBakery2bClientMutationRes,
      ),
    );
  }
  addCustomerConnectedWithBakery2BClient(): Observable<AddCustomerConnectedWithBakery2BClientMutationRes> {
    return this.actionListener('addCustomerConnectedWithBakery2BClient').pipe(
      switchMap(
        ({ params }) =>
          gqlClient(
            addCustomerConnectedWithBakery2BClient,
            params,
          ) as Observable<AddCustomerConnectedWithBakery2BClientMutation>,
      ),
      map(
        (data) =>
          data.wawiAssist
            ?.addWA_CustomerConnectedWithBakery2BClient as AddCustomerConnectedWithBakery2BClientMutationRes,
      ),
    );
  }
  deleteBakery2bClient(): Observable<DeleteBakery2bClientMutationRes> {
    return this.actionListener('deleteBakery2bClient').pipe(
      switchMap(
        ({ params }) =>
          gqlClient(deleteBakery2bClient, params) as Observable<DeleteBakery2bClientMutation>,
      ),
      map((data) => {
        return data.wawiAssist?.deleteWA_Bakery2bClient as DeleteBakery2bClientMutationRes;
      }),
    );
  }
  deleteCustomerConnectedWithBakery2BClient(): Observable<DeleteCustomerConnectedWithBakery2BClientMutationRes> {
    return this.actionListener('deleteCustomerConnectedWithBakery2BClient').pipe(
      switchMap(
        ({ params }) =>
          gqlClient(
            deleteCustomerConnectedWithBakery2BClient,
            params,
          ) as Observable<DeleteCustomerConnectedWithBakery2BClientMutation>,
      ),
      map((data) => {
        return data.wawiAssist
          ?.deleteWA_CustomerConnectedWithBakery2BClient as DeleteCustomerConnectedWithBakery2BClientMutationRes;
      }),
    );
  }
  listCustomerConnectedWithBakery2bClient(): Observable<ListCustomerConnectedWithBakery2bClientQueryRes> {
    return this.actionListener('listCustomerConnectedWithBakery2bClient').pipe(
      switchMap(
        ({ params }) =>
          gqlClient(
            listCustomerConnectedWithBakery2bClient,
            params,
          ) as Observable<ListCustomerConnectedWithBakery2bClientQuery>,
      ),
      map(
        (data) =>
          data.wawiAssist
            ?.listWA_CustomerConnectedWithBakery2bClient as ListCustomerConnectedWithBakery2bClientQueryRes,
      ),
    );
  }
  reinviteBakery2bClient(): Observable<ReinviteBakery2bClientMutationRes> {
    return this.actionListener('reinviteBakery2bClient').pipe(
      switchMap(
        ({ params }) =>
          gqlClient(reinviteBakery2bClient, params) as Observable<ReinviteBakery2bClientMutation>,
      ),
      map(
        (data) => data.wawiAssist?.reinviteWA_Bakery2bClient as ReinviteBakery2bClientMutationRes,
      ),
    );
  }
  listBakery2bStatistic(): Observable<ListBakery2bStatisticQueryRes> {
    return this.actionListener('listBakery2bStatistic').pipe(
      switchMap(
        ({ params }) =>
          gqlClient(listBakery2bStatistic, params) as Observable<ListBakery2bStatisticQuery>,
      ),
      map((data) => data.wawiAssist?.listWA_Bakery2bStatistic as ListBakery2bStatisticQueryRes),
    );
  }
}

class Bakery2bService extends Service<Actions> {
  pub = new PubImpl(this.stream$);
  sub = new SubImpl(this.stream$);
}

export const bakery2bService = new Bakery2bService();

export interface GetBakery2bSettingsQueryRes
  extends NonNullable<
    NonNullable<GetWa_Bakery2bSettingsQuery['wawiAssist']>['getWA_Bakery2bSettings']
  > {
  config?: NonNullable<NonNullable<GetWa_Bakery2bSettingsQuery['wawiAssist']>['getConfigData']>;
}

export interface SaveBakery2bSettingsInput extends Wa_SaveBakery2bSettings {
  config?: Pick<Wa_SaveConfigInput, 'isEditUsualOrdersInBakery2b'>;
}

export type SaveBakery2bSettingsMutationRes = NonNullable<
  NonNullable<SaveWa_Bakery2bSettingsMutation['wawiAssist']>['saveWA_Bakery2bSettings']
>;

export type SaveConfigDataMutationRes = NonNullable<
  NonNullable<SaveConfigDataMutation['wawiAssist']>['saveConfigData']
>;

export type SaveAllBakery2bSettingsMutationRes = {
  saveWA_Bakery2bSettings: SaveBakery2bSettingsMutationRes;
  saveConfigData: SaveConfigDataMutationRes;
};

export type ListBakery2bQueryRes = NonNullable<
  NonNullable<ListBakery2bQuery['wawiAssist']>['listWA_Bakery2b']
>;
export type SaveBakery2bClientMutationRes = NonNullable<
  NonNullable<SaveBakery2bClientMutation['wawiAssist']>['saveWA_Bakery2bClient']
>;
export type DeactivateBakery2bClientMutationRes = NonNullable<
  NonNullable<DeactivateBakery2bClientMutation['wawiAssist']>['deactivateWA_Bakery2bClient']
>;
export type ActivateBakery2bClientMutationRes = NonNullable<
  NonNullable<ActivateBakery2bClientMutation['wawiAssist']>['activateWA_Bakery2bClient']
>;
export type AddCustomerConnectedWithBakery2BClientMutationRes = NonNullable<
  NonNullable<
    AddCustomerConnectedWithBakery2BClientMutation['wawiAssist']
  >['addWA_CustomerConnectedWithBakery2BClient']
>;
export type DeleteBakery2bClientMutationRes = NonNullable<
  NonNullable<DeleteBakery2bClientMutation['wawiAssist']>['deleteWA_Bakery2bClient']
>;
export type DeleteCustomerConnectedWithBakery2BClientMutationRes = NonNullable<
  NonNullable<
    DeleteCustomerConnectedWithBakery2BClientMutation['wawiAssist']
  >['deleteWA_CustomerConnectedWithBakery2BClient']
>;
export type ListCustomerConnectedWithBakery2bClientQueryRes = NonNullable<
  NonNullable<
    ListCustomerConnectedWithBakery2bClientQuery['wawiAssist']
  >['listWA_CustomerConnectedWithBakery2bClient']
>;
export type ReinviteBakery2bClientMutationRes = NonNullable<
  NonNullable<ReinviteBakery2bClientMutation['wawiAssist']>['reinviteWA_Bakery2bClient']
>;
export type ListBakery2bStatisticQueryRes = NonNullable<
  NonNullable<ListBakery2bStatisticQuery['wawiAssist']>['listWA_Bakery2bStatistic']
>;

const hasOwnKeys = (obj: object): boolean => {
  return Object.keys(obj).length > 0;
};
